import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;
import CustomAPIConfig from "./CustomAPIConfig";
import CurlImporter from "./CurlImporter";
import styles from "./APIConfigIndex.module.css";
import { RiDeleteBin6Line } from "react-icons/ri";
import { BiSolidEdit } from "react-icons/bi";
import {
  FaAngleDown,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
  FaLink,
  FaUnlink,
  FaSyncAlt,
} from "react-icons/fa";
import { inferConfigFromSample } from "./utils/apiInference";
import { CurlImporterOverlay } from "./CurlImporterOverlay";
import { checkOutboundUrl } from "./utils/urlGuards";
import ReusablePopup from "../../../../ReusablePopup";

import ResourceScopeBadge from "./utils/ResourceScopeBadge";
import {
  attachScope,
  detachScope,
  listAttachments,
  getAttachmentsByResource,
} from "./utils/scopeAttachmentsApi";
import {
  classifyGuardRefusal,
  formatAttachedAppChips,
  formatResourceInUseError,
} from "./utils/scopeAttachmentValidation";
import ScopeTabs from "./ScopeTabs";

const API_TYPE = {
  REGULAR: "REGULAR",
  PRIMARY: "PRIMARY",
  SECONDARY_1: "SECONDARY_1",
  SECONDARY_2: "SECONDARY_2",
};

const SCOPE = {
  APP: "app",
  TENANT: "tenant",
};

const ENTRY_TYPES = {
  LOCAL_APP: "local_app_level",
  DEFAULT_APP: "default_app_level",
  DEFAULT_GLOBAL: "default_global_level",
};

const defaultSample = {
  sampleUrl: "",
  sampleMethod: "POST",
  sampleHeaders: {},
  sampleQueryParams: {},
  samplePayload: "{}",
};

const defaultInferred = {
  apiUrl: "",
  method: "POST",
  headers: {},
  queryParams: {},
  payloadTemplate: "{}",
  response_template: {},
  post_processing_text:
    "Thank you for your interest. Our team will contact you shortly.",
  email_template: "THANK YOU",
  lead_destination_type: "custom",
  webhook_event_name: "",
  webhook_timeout_seconds: 600,
  resp_data: {},
  transformation_steps: [],
  actions: [],
  original_curl_config: null,
};

const DEFAULT_RESPONSE_TEMPLATE = "{{ response_data | dump | safe }}";

const PLACEHOLDER_RE = /\{\{\s*[^}]+?\s*\}\}/;

const isPlaceholderValue = (v) => {
  if (typeof v === "string") return PLACEHOLDER_RE.test(v);
  if (Array.isArray(v)) return v.some(isPlaceholderValue);
  if (v && typeof v === "object") {
    return Object.values(v).some(isPlaceholderValue);
  }
  return false;
};

const stripPlaceholders = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(stripPlaceholders).filter((v) => v !== undefined);
  }
  if (obj && typeof obj === "object") {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (isPlaceholderValue(v)) continue;
      out[k] = stripPlaceholders(v);
    }
    return out;
  }
  return obj;
};

const parseConcreteValues = (payloadString) => {
  if (!payloadString || typeof payloadString !== "string") return {};
  const trimmed = payloadString.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return stripPlaceholders(parsed);
  } catch {
    return {};
  }
};

const isStatusConfigured = (respData, status) =>
  Boolean(respData?.[status]?.req_uid);

const getUnconfiguredStatuses = (respData, requiredStatuses) => {
  const out = [];
  for (const status of requiredStatuses) {
    if (!isStatusConfigured(respData, status)) {
      out.push(status);
    }
  }
  return out;
};

const readDeleteError = async (resp, fallbackLabel = "Deletion failed") => {
  let msg = `${fallbackLabel} (HTTP ${resp.status}).`;
  try {
    const body = await resp.json();
    msg = body?.error || body?.detail || body?.message || msg;
  } catch (_) { }
  return msg;
};

// ⭐ NEW: Component for lazy-loading text answers per status
// ⭐ NEW: Component for lazy-loading text answers per status
const TextAnswerAccordion = ({
  status,
  inferredForm,
  sampleResponses,
  setInferredForm,
  apiConfigId,
  region,
  token,
  backend_api_base_url,
  onOpenScreenEditor,
}) => {
  const isConfigured = !!inferredForm?.resp_data?.[status]?.req_uid;

  // ⭐ FIX: Unconfigured statuses are open by default so the user can actually type.
  // Configured statuses are closed by default to save API calls.
  const [isOpen, setIsOpen] = useState(!isConfigured);
  const [loading, setLoading] = useState(false);
  const [fetchedText, setFetchedText] = useState("");

  const currentText = inferredForm?.resp_data?.[status]?.text_answer_template || "";

  // Lazy load the template only if it's already configured
  const handleToggle = async () => {
    if (!isOpen && isConfigured && !fetchedText) {
      setLoading(true);
      try {
        const resp = await fetch(
          `https://${region}.autobot2.${backend_api_base_url}/final_responses?config_id=${apiConfigId}&status_code=${status}`,
          { headers: { Authorization: token } }
        );
        if (resp.ok) {
          const data = await resp.json();
          const tmpl = data.items?.find((i) => i.is_active) || data.items?.[0];
          if (tmpl) {
            setFetchedText(tmpl.text_answer_template || "");
          }
        }
      } catch (e) {
        console.error("Failed to fetch template", e);
      } finally {
        setLoading(false);
      }
    }
    setIsOpen(!isOpen);
  };

  let sampleFields = [];
  try {
    const sampleBody =
      inferredForm?.sample_responses_by_status?.[status] ||
      sampleResponses.find((r) => r.status === status)?.body;
    if (sampleBody) {
      const parsed = JSON.parse(sampleBody);
      sampleFields = Object.keys(parsed).map((k) => `response_data.${k}`);
    }
  } catch (e) {
    /* ignore */
  }

  return (
    <div
      style={{
        marginTop: 12,
        padding: isConfigured ? 12 : 0,
        background: isConfigured ? "#f8fafc" : "transparent",
        border: isConfigured ? "1px solid #e2e8f0" : "none",
        borderRadius: 6,
      }}
    >
      {isConfigured ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: "#4a5568" }}>
            Text Answer Template for Status {status}
          </span>
          <button
            type="button"
            onClick={handleToggle}
            style={{
              padding: "4px 12px",
              fontSize: 12,
              fontWeight: 600,
              color: isOpen ? "#e53e3e" : "#6b46c1",
              backgroundColor: isOpen ? "#fff5f5" : "#faf5ff",
              border: `1px solid ${isOpen ? "#feb2b2" : "#d6bcfa"}`,
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            {isOpen ? "Close Editor" : "📝 Show/Edit Text Answer"}
          </button>
        </div>
      ) : (
        <div style={{ padding: 12, background: "#fff", border: "1px solid #ffb74d", borderRadius: 6 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#E65100" }}>
            ⚠️ Text Answer not yet created for Status {status}
          </div>
        </div>
      )}

      {isOpen && (
        <div style={{ marginTop: 12 }}>
          {loading ? (
            <div style={{ fontSize: 12, color: "#718096" }}>Loading saved template...</div>
          ) : (
            <>
              <textarea
                rows={3}
                placeholder="Write a plain text answer, e.g. The inventory for {{ response_data.name }} is..."
                style={{
                  width: "100%",
                  padding: 8,
                  fontSize: 13,
                  fontFamily: "monospace",
                  border: "1px solid #cbd5e1",
                  borderRadius: 4,
                  boxSizing: "border-box",
                }}
                value={isConfigured ? (currentText || fetchedText) : (currentText || fetchedText)}
                onChange={(e) => {
                  const val = e.target.value;
                  setInferredForm((prev) => {
                    const currentRespData = prev.resp_data || {};
                    const currentStatusData = currentRespData[status] || {};
                    return {
                      ...prev,
                      resp_data: {
                        ...currentRespData,
                        [status]: {
                          ...currentStatusData,
                          req_uid: currentStatusData.req_uid || `temp_${Date.now()}`,
                          text_answer_template: val,
                          resp_template: currentStatusData.resp_template || {},
                          controls: currentStatusData.controls || {},
                          transformation_steps: currentStatusData.transformation_steps || [],
                          actions: currentStatusData.actions || [],
                        },
                      },
                    };
                  });
                }}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                <span style={{ fontSize: 11, color: "#718096", alignSelf: "center", marginRight: 4 }}>
                  Insert field:
                </span>
                {sampleFields.map((field) => (
                  <button
                    key={field}
                    type="button"
                    onClick={() => {
                      setInferredForm((prev) => {
                        const currentRespData = prev.resp_data || {};
                        const currentStatusData = currentRespData[status] || {};
                        const prevText = currentStatusData.text_answer_template || "";
                        return {
                          ...prev,
                          resp_data: {
                            ...currentRespData,
                            [status]: {
                              ...currentStatusData,
                              req_uid: currentStatusData.req_uid || `temp_${Date.now()}`,
                              text_answer_template: prevText + `{{ ${field} }} `,
                              resp_template: currentStatusData.resp_template || {},
                              controls: currentStatusData.controls || {},
                              transformation_steps: currentStatusData.transformation_steps || [],
                              actions: currentStatusData.actions || [],
                            },
                          },
                        };
                      });
                    }}
                    style={{
                      padding: "2px 8px",
                      fontSize: 11,
                      background: "#ebf8ff",
                      color: "#2b6cb0",
                      border: "1px solid #90cdf4",
                      borderRadius: 12,
                      cursor: "pointer",
                    }}
                  >
                    {field}
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => onOpenScreenEditor(status)}
                  style={{
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6b46c1",
                    backgroundColor: "#faf5ff",
                    border: "1px solid #d6bcfa",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  🎨 Or design a structured screen instead
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const UnconfiguredTemplatesBox = ({
  unconfiguredStatuses,
  configuredStatuses,
  inferredForm,
  sampleResponses,
  isTemplateValid,
  requiredStatuses,
  setInferredForm,
  onOpenScreenEditor,
  apiConfigId,
  region,
  token,
  backend_api_base_url,
}) => {
  const allStatusesToShow = [...new Set([...unconfiguredStatuses, ...configuredStatuses])];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {unconfiguredStatuses.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            padding: "12px",
            backgroundColor: "#FFF3E0",
            borderRadius: "4px",
            border: "1px solid #FF9800",
          }}
        >
          <FaInfoCircle size={18} style={{ marginTop: "2px", color: "#FF9800", flexShrink: 0 }} />
          <span style={{ color: "#E65100", fontWeight: "bold" }}>
            ⚠️ Response template not yet created for status
            {unconfiguredStatuses.length === 1 ? "" : "es"}:{" "}
            <code style={{ marginLeft: 4 }}>{unconfiguredStatuses.join(", ")}</code>
          </span>
        </div>
      )}

      {allStatusesToShow.map((status) => (
        <TextAnswerAccordion
          key={status}
          status={status}
          inferredForm={inferredForm}
          sampleResponses={sampleResponses}
          setInferredForm={setInferredForm}
          apiConfigId={apiConfigId}
          region={region}
          token={token}
          backend_api_base_url={backend_api_base_url}
          onOpenScreenEditor={onOpenScreenEditor}
        />
      ))}
    </div>
  );
};

export default function APIConfigIndex({ appData }) {
  const history = useHistory();
  const region = appData?.region?.toLowerCase();
  const appName = appData?.app_name;
  const token = localStorage.getItem("token") || "";

  const isGlobalContext = !appName;

  const [activeTab, setActiveTab] = useState(
    isGlobalContext ? SCOPE.TENANT : SCOPE.TENANT,
  );

  const [tenantConfigs, setTenantConfigs] = useState([]);
  const [appConfigs, setAppConfigs] = useState([]);
  const [attachedIds, setAttachedIds] = useState(() => new Set());
  const [attachmentsByConfig, setAttachmentsByConfig] = useState(
    () => new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingApiConfigId, setEditingApiConfigId] = useState(null);
  const [showCreateConfig, setShowCreateConfig] = useState(false);
  const [curlImportOpen, setCurlImportOpen] = useState(false);
  const [originalCurlConfig, setOriginalCurlConfig] = useState(null);
  const [isCurlImportCompleted, setIsCurlImportCompleted] = useState(false);

  const [configScope, setConfigScope] = useState(SCOPE.APP);

  const [previousSampleResponses, setPreviousSampleResponses] = useState([]);

  const [sampleForm, setSampleForm] = useState(defaultSample);
  const [inferredForm, setInferredForm] = useState(defaultInferred);
  const [sampleResponses, setSampleResponses] = useState([
    { status: "200", body: "" },
  ]);
  const [editorOpenCount, setEditorOpenCount] = useState(0);

  const [configActiveTab, setConfigActiveTab] = useState("sample");
  const [inferredReady, setInferredReady] = useState(false);

  const customConfigRef = useRef(null);
  const editorModalRef = useRef(null);
  const editAbortControllerRef = useRef(null);

  const [confirmPopup, setConfirmPopup] = useState({
    isOpen: false,
    heading: "",
    subtext: "",
    paragraph: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    onConfirm: null,
    iconColor: "#dc2626",
    iconBG: "#ffc4c4",
    icon: <RiDeleteBin6Line size={28} />,
  });

  const openConfirmPopup = ({
    heading,
    subtext = "",
    paragraph = "",
    confirmText = "Confirm",
    cancelText = "Cancel",
    icon = <RiDeleteBin6Line size={28} />,
    iconColor = "#dc2626",
    iconBG = "#ffc4c4",
    onConfirm,
  }) => {
    setConfirmPopup({
      isOpen: true,
      heading,
      subtext,
      paragraph,
      confirmText,
      cancelText,
      icon,
      iconColor,
      iconBG,
      onConfirm,
    });
  };
  const closeConfirmPopup = () =>
    setConfirmPopup((p) => ({ ...p, isOpen: false, onConfirm: null }));

  const openAlert = ({
    heading = "",
    subtext = "",
    paragraph = "",
    confirmText = "OK",
    icon = null,
    iconColor = "#16a34a",
    iconBG = "#dcfce7",
  }) => {
    setConfirmPopup({
      isOpen: true,
      heading,
      subtext,
      paragraph,
      confirmText,
      cancelText: "",
      icon,
      iconColor,
      iconBG,
      onConfirm: closeConfirmPopup,
    });
  };

  const openGuardPopup = ({ refusal, onForce }) => {
    const BodyNode = (
      <div style={{ textAlign: "left", lineHeight: 1.6 }}>
        {refusal.kind === "attached-in-use" && (
          <>
            <div style={{ marginBottom: 8 }}>{refusal.message}</div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 6,
              }}
            >
              {formatAttachedAppChips(
                refusal.apps.map((a) => ({ app_name: a })),
              ).map((chip) => (
                <button
                  key={chip.app_name}
                  type="button"
                  onClick={async () => {
                    try {
                      await detachScope({
                        region,
                        appName: chip.app_name,
                        resourceType: "api_config",
                        tenantResourceId: editingApiConfigId,
                      });
                      openAlert({
                        heading: "Detached",
                        paragraph: `${chip.app_name} has been detached from this config.`,
                        icon: <FaCheckCircle size={28} />,
                      });
                      closeConfirmPopup();
                      fetchApiConfigs();
                    } catch (e) {
                      openAlert({
                        heading: "Couldn't detach",
                        paragraph: e.message,
                        icon: <FaTimesCircle size={28} />,
                        iconColor: "#dc2626",
                        iconBG: "#fee2e2",
                      });
                    }
                  }}
                  style={{
                    padding: "4px 10px",
                    fontSize: 12,
                    borderRadius: 4,
                    border: "1px solid #c4b5fd",
                    background: "#f5f3ff",
                    color: "#6b46c1",
                    cursor: "pointer",
                  }}
                >
                  Detach {chip.app_name}
                </button>
              ))}
            </div>
            {refusal.canForce && onForce && (
              <div style={{ marginTop: 12, fontSize: 12, color: "#718096" }}>
                To update this config anyway, click <b>Force save</b>. Every
                attached app will see the change immediately.
              </div>
            )}
          </>
        )}
        {refusal.kind === "referenced" && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              margin: 0,
              fontSize: 13,
            }}
          >
            {formatResourceInUseError({
              blocking_refs: refusal.refs,
              error: refusal.message,
            })}
          </pre>
        )}
        {refusal.kind !== "attached-in-use" &&
          refusal.kind !== "referenced" && <div>{refusal.message}</div>}
      </div>
    );

    setConfirmPopup({
      isOpen: true,
      heading:
        refusal.kind === "attached-in-use"
          ? "This config is attached to other apps"
          : refusal.kind === "referenced"
            ? "This config is in use"
            : "Couldn't complete the request",
      subtext: "",
      paragraph: BodyNode,
      confirmText:
        refusal.kind === "attached-in-use" && refusal.canForce
          ? "Force save"
          : "OK",
      cancelText:
        refusal.kind === "attached-in-use" && refusal.canForce
          ? "Cancel"
          : "",
      icon:
        refusal.kind === "attached-in-use" ? (
          <FaExclamationTriangle size={28} />
        ) : (
          <FaTimesCircle size={28} />
        ),
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      onConfirm:
        refusal.kind === "attached-in-use" && refusal.canForce && onForce
          ? () => {
            closeConfirmPopup();
            onForce();
          }
          : closeConfirmPopup,
    });
  };

  const fetchApiConfigs = useCallback(async () => {
    if (!region) return;
    setLoading(true);
    try {
      const appParams = new URLSearchParams();
      if (appName) appParams.set("app_name", appName);
      appParams.set("include_tenant", "false");

      const tenantParams = new URLSearchParams();
      tenantParams.set("scope", "tenant");
      if (appName) tenantParams.set("app_name", appName);

      const [appResp, tenantResp, attachResp] = await Promise.all([
        fetch(
          `https://${region}.autobot2.${backend_api_base_url}/api_config?${appParams.toString()}`,
          { headers: { Authorization: token } },
        ),
        fetch(
          `https://${region}.autobot2.${backend_api_base_url}/api_config?${tenantParams.toString()}`,
          { headers: { Authorization: token } },
        ),
        appName
          ? listAttachments({
            region,
            appName,
            resourceType: "api_config",
          })
          : getAttachmentsByResource({
            region,
            resourceType: "api_config",
          }).then((map) => {
            const attachments = [];
            for (const bucket of map.values()) {
              for (const a of bucket.attachments) {
                attachments.push(a);
              }
            }
            return { attachments, count: attachments.length };
          }),
      ]);

      if (!appResp.ok || !tenantResp.ok) {
        setError("Failed to load API configurations. Please try again.");
        return;
      }

      const appDataRes = await appResp.json();
      const tenantDataRes = await tenantResp.json();
      const rawApp = appDataRes.configs || [];
      const rawTenant = tenantDataRes.configs || [];

      const appRegular = rawApp.filter(
        (cfg) => cfg.api_type === API_TYPE.REGULAR && cfg.scope === SCOPE.APP,
      );
      const tenantRegular = rawTenant.filter(
        (cfg) =>
          cfg.api_type === API_TYPE.REGULAR && cfg.scope === SCOPE.TENANT,
      );

      setAppConfigs(appRegular);
      setTenantConfigs(tenantRegular);

      const ids = new Set();
      const grouped = new Map();
      for (const row of attachResp.attachments || []) {
        const rid = row.tenant_resource_id;
        if (!rid) continue;
        if (!grouped.has(rid)) {
          grouped.set(rid, { app_names: [], attachments: [] });
        }
        const bucket = grouped.get(rid);
        bucket.attachments.push(row);
        if (row.app_name && !bucket.app_names.includes(row.app_name)) {
          bucket.app_names.push(row.app_name);
        }
        if (appName && row.app_name === appName) ids.add(rid);
      }
      for (const bucket of grouped.values()) bucket.app_names.sort();

      setAttachedIds(ids);
      setAttachmentsByConfig(grouped);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [region, appName, token]);

  useEffect(() => {
    fetchApiConfigs();
  }, [fetchApiConfigs]);

  useEffect(() => {
    if (!showCreateConfig || !editorModalRef.current) return;
    const t = setTimeout(() => {
      editorModalRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 60);
    return () => clearTimeout(t);
  }, [showCreateConfig, editorOpenCount]);

  const handleAttach = useCallback(
    async (config) => {
      try {
        await attachScope({
          region,
          appName,
          resourceType: "api_config",
          tenantResourceId: config.api_config_id,
        });
        openAlert({
          heading: "Attached",
          paragraph: `This config is now available to every form, skill, and chain on ${appName}.`,
          icon: <FaCheckCircle size={28} />,
        });
        fetchApiConfigs();
      } catch (err) {
        const refusal = classifyGuardRefusal({ error: err.message });
        openAlert({
          heading: "Couldn't attach",
          paragraph: refusal.message || err.message,
          icon: <FaTimesCircle size={28} />,
          iconColor: "#dc2626",
          iconBG: "#fee2e2",
        });
      }
    },
    [region, appName, fetchApiConfigs],
  );

  const handleDetach = useCallback(
    (config) => {
      openConfirmPopup({
        heading: "Detach this config from this app?",
        subtext: `Config: ${config.api_config?.url || config.api_config_id}`,
        paragraph:
          "The config stays on the tenant — other apps that use it are unaffected. This app will stop seeing it in drop-downs, and any form, skill, or chain still pointing at it will fail on next run.",
        confirmText: "Detach",
        cancelText: "Cancel",
        icon: <FaUnlink size={28} />,
        iconColor: "#d97706",
        iconBG: "#fef3c7",
        onConfirm: async () => {
          closeConfirmPopup();
          try {
            await detachScope({
              region,
              appName,
              resourceType: "api_config",
              tenantResourceId: config.api_config_id,
            });
            openAlert({
              heading: "Detached",
              paragraph: "This app no longer has access to this config.",
              icon: <FaCheckCircle size={28} />,
            });
            fetchApiConfigs();
          } catch (err) {
            openAlert({
              heading: "Couldn't detach",
              paragraph: err.message,
              icon: <FaTimesCircle size={28} />,
              iconColor: "#dc2626",
              iconBG: "#fee2e2",
            });
          }
        },
      });
    },
    [region, appName, fetchApiConfigs],
  );

  const handleCreateResponseTemplate = (
    configId,
    kbId,
    statusCode,
    sampleResponse = null,
  ) => {
    if (!statusCode) {
      statusCode = prompt(
        "Enter the HTTP status code (e.g., 200, 201, 404) that this template should handle:",
      );
      if (!statusCode) return;
    }
    const params = new URLSearchParams({
      scenario: ENTRY_TYPES.LOCAL_APP,
      config_id: configId,
      kb_id: kbId,
      status_code: statusCode,
      mode: "screen",
      template: "{{ response_data | dump | safe }}",
    });
    if (sampleResponse) params.set("sample_response", sampleResponse);
    history.push(`/response_format_setter?${params.toString()}`);
  };

  const handleViewAllTemplates = (configId, kbId) => {
    const params = new URLSearchParams({
      config_id: configId,
      kb_id: kbId,
    });
    history.push(`/response_templates?${params.toString()}`);
  };

  const validateRegularConfigAuth = (form) => {
    return true;
  };

  const validateAuthorizationHeader = (headers) => {
    for (const [key, value] of Object.entries(headers)) {
      if (key.toLowerCase() === "authorization") {
        if (!value.includes("{{")) {
          openAlert({
            heading: "⚠️ Authorization header must use {{ token }}",
            paragraph:
              "The Authorization header must use the {{ token }} placeholder.\n\n" +
              "The system will automatically replace {{ token }} with the valid access token when making the request.\n\n" +
              "Example: 'Bearer {{ token }}'\n\n" +
              "Please replace your literal token value with {{ token }}.",
            icon: <FaExclamationTriangle size={28} />,
            iconColor: "#d97706",
            iconBG: "#fef3c7",
          });
          return false;
        }
      }
    }
    return true;
  };

  const createHandlers = (
    sampleForm,
    setSampleForm,
    sampleResponses,
    setSampleResponses,
    inferredForm,
    setInferredForm,
  ) => {
    const onSampleChange = (field, value) =>
      setSampleForm((prev) => ({ ...prev, [field]: value }));
    const onSampleHeaderChange = (key, value) =>
      setSampleForm((prev) => ({
        ...prev,
        sampleHeaders: { ...prev.sampleHeaders, [key]: value },
      }));
    const onSampleAddHeader = () =>
      setSampleForm((prev) => ({
        ...prev,
        sampleHeaders: { ...prev.sampleHeaders, "": "" },
      }));
    const onSampleRemoveHeader = (key) => {
      setSampleForm((prev) => {
        const h = { ...prev.sampleHeaders };
        delete h[key];
        return { ...prev, sampleHeaders: h };
      });
    };
    const onSampleQueryChange = (key, value) =>
      setSampleForm((prev) => ({
        ...prev,
        sampleQueryParams: { ...prev.sampleQueryParams, [key]: value },
      }));
    const onSampleAddQuery = () =>
      setSampleForm((prev) => ({
        ...prev,
        sampleQueryParams: { ...prev.sampleQueryParams, "": "" },
      }));
    const onSampleRemoveQuery = (key) => {
      setSampleForm((prev) => {
        const q = { ...prev.sampleQueryParams };
        delete q[key];
        return { ...prev, sampleQueryParams: q };
      });
    };
    const onSampleResponseChange = (index, field, value) => {
      const updated = [...sampleResponses];
      updated[index] = { ...updated[index], [field]: value };
      setSampleResponses(updated);
    };
    const onAddSampleResponse = () =>
      setSampleResponses((prev) => [...prev, { status: "200", body: "" }]);
    const onRemoveSampleResponse = (index) => {
      if (sampleResponses.length <= 1) return;
      setSampleResponses((prev) => prev.filter((_, i) => i !== index));
    };
    const onInferredChange = (field, value) =>
      setInferredForm((prev) => ({ ...prev, [field]: value }));
    const onInferredHeaderChange = (key, value) =>
      setInferredForm((prev) => ({
        ...prev,
        headers: { ...prev.headers, [key]: value },
      }));
    const onInferredAddHeader = () =>
      setInferredForm((prev) => ({
        ...prev,
        headers: { ...prev.headers, "": "" },
      }));
    const onInferredRemoveHeader = (key) => {
      setInferredForm((prev) => {
        const h = { ...prev.headers };
        delete h[key];
        return { ...prev, headers: h };
      });
    };
    const onInferredQueryChange = (key, value) =>
      setInferredForm((prev) => ({
        ...prev,
        queryParams: { ...prev.queryParams, [key]: value },
      }));
    const onInferredAddQuery = () =>
      setInferredForm((prev) => ({
        ...prev,
        queryParams: { ...prev.queryParams, "": "" },
      }));
    const onInferredRemoveQuery = (key) => {
      setInferredForm((prev) => {
        const q = { ...prev.queryParams };
        delete q[key];
        return { ...prev, queryParams: q };
      });
    };
    const onInsertField = (_, fieldName) => {
      setInferredForm((prev) => {
        const current = prev.payloadTemplate || "{}";
        const newText =
          current.slice(0, -1) +
          (current.length > 2 ? "," : "") +
          `"${fieldName}": "{{ ${fieldName} }}"}`;
        return { ...prev, payloadTemplate: newText };
      });
    };
    return {
      onSampleChange,
      onSampleHeaderChange,
      onSampleAddHeader,
      onSampleRemoveHeader,
      onSampleQueryChange,
      onSampleAddQuery,
      onSampleRemoveQuery,
      onSampleResponseChange,
      onAddSampleResponse,
      onRemoveSampleResponse,
      onInferredChange,
      onInferredHeaderChange,
      onInferredAddHeader,
      onInferredRemoveHeader,
      onInferredQueryChange,
      onInferredAddQuery,
      onInferredRemoveQuery,
      onInsertField,
    };
  };

  const regularHandlers = createHandlers(
    sampleForm,
    setSampleForm,
    sampleResponses,
    setSampleResponses,
    inferredForm,
    setInferredForm,
  );

  const handleSamplePayloadSyncFromChild = useCallback((cleanedValues) => {
    if (!cleanedValues || typeof cleanedValues !== "object") return;
    setInferredForm((prev) => {
      const current = prev.original_curl_config || {};
      const currentPayload = current.payload || {};
      let same = false;
      try {
        same = JSON.stringify(currentPayload) === JSON.stringify(cleanedValues);
      } catch {
        same = false;
      }
      if (same) return prev;
      return {
        ...prev,
        original_curl_config: {
          ...current,
          url: current.url || prev.apiUrl || "",
          method: current.method || prev.method || "POST",
          headers:
            current.headers && Object.keys(current.headers).length > 0
              ? current.headers
              : prev.headers || {},
          queryParams:
            current.queryParams && Object.keys(current.queryParams).length > 0
              ? current.queryParams
              : prev.queryParams || {},
          payloadTemplate:
            current.payloadTemplate || prev.payloadTemplate || "{}",
          payload: cleanedValues,
        },
      };
    });
  }, []);

  const haveSampleResponsesChanged = (current, previous) => {
    if (!previous || previous.length === 0) return true;
    if (current.length !== previous.length) return true;
    for (let i = 0; i < current.length; i++) {
      if (current[i].status !== previous[i].status) return true;
      if (current[i].body !== previous[i].body) return true;
    }
    return false;
  };

  const inferVariables = (
    sampleFormObj,
    sampleResponsesArr,
    setInferred,
    setReady,
    setActiveTab,
    preserveExistingTemplate = false,
  ) => {
    const inferred = inferConfigFromSample(sampleFormObj);

    const newTemplateObj = {};
    sampleResponsesArr.forEach(({ status }) => {
      const key = status.trim();
      if (key) {
        newTemplateObj[key] = DEFAULT_RESPONSE_TEMPLATE;
      }
    });

    const concretePayload = parseConcreteValues(sampleFormObj.samplePayload);
    const hasConcretePayload = Object.keys(concretePayload).length > 0;

    const buildOriginalCurl = (prevOriginal) => {
      const base = prevOriginal || {};
      return {
        url: sampleFormObj.sampleUrl || base.url || "",
        method: sampleFormObj.sampleMethod || base.method || "POST",
        headers:
          Object.keys(sampleFormObj.sampleHeaders || {}).length > 0
            ? sampleFormObj.sampleHeaders
            : base.headers || {},
        queryParams:
          Object.keys(sampleFormObj.sampleQueryParams || {}).length > 0
            ? sampleFormObj.sampleQueryParams
            : base.queryParams || {},
        payloadTemplate:
          sampleFormObj.samplePayload && sampleFormObj.samplePayload !== "{}"
            ? sampleFormObj.samplePayload
            : base.payloadTemplate || "{}",
        payload: hasConcretePayload ? concretePayload : base.payload || {},
        sampleResponses: sampleResponsesArr,
      };
    };

    if (preserveExistingTemplate) {
      setInferred((prev) => {
        const existingTemplate = prev.response_template || {};
        const mergedTemplate = { ...existingTemplate };
        Object.keys(newTemplateObj).forEach((key) => {
          if (!existingTemplate[key]) {
            mergedTemplate[key] = newTemplateObj[key];
          }
        });
        return {
          ...prev,
          ...inferred,
          response_template: mergedTemplate,
          original_curl_config: buildOriginalCurl(prev.original_curl_config),
        };
      });
    } else {
      inferred.response_template = newTemplateObj;
      setInferred((prev) => ({
        ...prev,
        ...inferred,
        original_curl_config: buildOriginalCurl(prev.original_curl_config),
      }));
    }

    setReady(true);
    setActiveTab("inferred");
  };

  const handleCurlImport = useCallback(
    (
      intentName,
      parsedConfig,
      sampleResponsesFromParser,
      responseTemplateObj,
    ) => {
      setOriginalCurlConfig({
        url: parsedConfig.url,
        method: parsedConfig.method,
        headers: parsedConfig.headers,
        queryParams: parsedConfig.queryParams,
        payload: parsedConfig.fieldValues || {},
        payloadTemplate: parsedConfig.payloadTemplate,
        sampleResponses: sampleResponsesFromParser,
      });

      setSampleForm({
        sampleUrl: parsedConfig.url || "",
        sampleMethod: parsedConfig.method || "POST",
        sampleHeaders: parsedConfig.headers || {},
        sampleQueryParams: parsedConfig.queryParams || {},
        samplePayload: parsedConfig.payloadTemplate || "{}",
      });
      setSampleResponses(
        sampleResponsesFromParser.length
          ? sampleResponsesFromParser
          : [{ status: "200", body: "" }],
      );
      setPreviousSampleResponses(sampleResponsesFromParser);
      setCurlImportOpen(false);
      setInferredReady(false);
      setConfigActiveTab("sample");
      setIsCurlImportCompleted(true);
    },
    [],
  );

  const openCreate = () => {
    setEditingApiConfigId(null);
    setConfigScope(isGlobalContext ? SCOPE.TENANT : SCOPE.APP);
    setSampleForm(defaultSample);
    setInferredForm(defaultInferred);
    setSampleResponses([{ status: "200", body: "" }]);
    setPreviousSampleResponses([]);
    setOriginalCurlConfig(null);
    setCurlImportOpen(false);
    setInferredReady(false);
    setConfigActiveTab("sample");
    setShowCreateConfig(true);
    setIsCurlImportCompleted(false);
    setEditorOpenCount((c) => c + 1);
  };

  const openEdit = async (config) => {
    const cfg = config.api_config || {};
    const original = cfg.original_curl_config;

    if (config.scope === SCOPE.TENANT && !isGlobalContext) {
      openAlert({
        heading: "Shared config",
        paragraph:
          "This config is published by your tenant admin. You can attach or detach it, but only the tenant admin can edit it.",
        icon: <FaInfoCircle size={28} />,
        iconColor: "#2b6cb0",
        iconBG: "#ebf8ff",
      });
      return;
    }

    setConfigScope(config.scope || SCOPE.APP);
    setSampleForm({
      sampleUrl: cfg.url || original?.url || "",
      sampleMethod: cfg.method || original?.method || "POST",
      sampleHeaders:
        Object.keys(cfg.headers || {}).length > 0
          ? cfg.headers
          : original?.headers || {},
      sampleQueryParams:
        Object.keys(cfg.query_params || {}).length > 0
          ? cfg.query_params
          : original?.queryParams || {},
      samplePayload:
        cfg.payload_template && cfg.payload_template !== "{}"
          ? cfg.payload_template
          : original?.payloadTemplate || "{}",
    });

    const existingTemplate = cfg.response_template || {};

    setInferredForm({
      apiUrl: cfg.url || "",
      method: cfg.method || "POST",
      headers: cfg.headers || {},
      queryParams: cfg.query_params || {},
      payloadTemplate: cfg.payload_template || "{}",
      response_template: existingTemplate,
      post_processing_text: cfg.post_processing_text || "",
      email_template: cfg.email_template || "THANK YOU",
      lead_destination_type: cfg.lead_destination_type || "custom",
      webhook_event_name: cfg.webhook_event_name || "",
      webhook_timeout_seconds: cfg.webhook_timeout_seconds || 600,
      resp_data: cfg.resp_data || {},
      transformation_steps: cfg.transformation_steps || [],
      original_curl_config: original || null,
    });

    const sampleMap = cfg.sample_responses_by_status || {};
    const sr = Object.entries(sampleMap).map(([status, body]) => ({
      status,
      body: typeof body === "string" ? body : "",
    }));
    setSampleResponses(sr.length ? sr : [{ status: "200", body: "" }]);
    setPreviousSampleResponses(sr.length ? sr : [{ status: "200", body: "" }]);

    setOriginalCurlConfig(original || null);
    setEditingApiConfigId(config.api_config_id);
    setCurlImportOpen(false);
    setInferredReady(true);
    setConfigActiveTab("sample");
    setShowCreateConfig(true);
    setIsCurlImportCompleted(true);
    setEditorOpenCount((c) => c + 1);
  };

  const closeEditor = () => {
    setShowCreateConfig(false);
    setCurlImportOpen(false);
    setPreviousSampleResponses([]);
    setIsCurlImportCompleted(false);
  };

  const buildSavePayload = (force = false) => {
    const requiredStatusesForSave = Array.from(
      new Set([
        ...sampleResponses.map((r) => (r.status || "").trim()).filter(Boolean),
        ...Object.keys(inferredForm.response_template || {}),
      ]),
    );

    const fromChild = inferredForm.original_curl_config || {};
    const sanitizedFromChild = stripPlaceholders(fromChild.payload || {});
    const hasSanitizedFromChild = Object.keys(sanitizedFromChild).length > 0;
    const parsedFromSample = parseConcreteValues(sampleForm.samplePayload);
    const hasParsedFromSample = Object.keys(parsedFromSample).length > 0;

    const payloadValues = hasSanitizedFromChild
      ? sanitizedFromChild
      : hasParsedFromSample
        ? parsedFromSample
        : {};

    const kbIdForTemplates =
      appData?.tenant_id && appData?.app_name
        ? `${appData.tenant_id}__${appData.app_name}`
        : appData?.tenant_id
          ? `${appData.tenant_id}__global`
          : "";

    const apiConfigPayload = {
      sample_responses_by_status: sampleResponses.reduce(
        (acc, { status, body }) => {
          acc[status] = body;
          return acc;
        }, {},
      ),
      url: inferredForm.apiUrl,
      method: inferredForm.method,
      headers: inferredForm.headers,
      query_params: inferredForm.queryParams,
      payload_template: inferredForm.payloadTemplate,
      response_template: inferredForm.response_template,
      post_processing_text: inferredForm.post_processing_text,
      email_template: inferredForm.email_template,
      lead_destination_type: inferredForm.lead_destination_type,
      original_curl_config: {
        url: fromChild.url || sampleForm.sampleUrl || inferredForm.apiUrl || "",
        method:
          fromChild.method ||
          sampleForm.sampleMethod ||
          inferredForm.method ||
          "POST",
        headers:
          fromChild.headers && Object.keys(fromChild.headers).length > 0
            ? fromChild.headers
            : Object.keys(sampleForm.sampleHeaders || {}).length > 0
              ? sampleForm.sampleHeaders
              : inferredForm.headers || {},
        queryParams:
          fromChild.queryParams && Object.keys(fromChild.queryParams).length > 0
            ? fromChild.queryParams
            : Object.keys(sampleForm.sampleQueryParams || {}).length > 0
              ? sampleForm.sampleQueryParams
              : inferredForm.queryParams || {},
        payloadTemplate:
          fromChild.payloadTemplate ||
          (sampleForm.samplePayload && sampleForm.samplePayload !== "{}"
            ? sampleForm.samplePayload
            : inferredForm.payloadTemplate || "{}"),
        payload: payloadValues,
        sampleResponses,
      },
      ...(inferredForm.webhook_event_name && {
        webhook_event_name: inferredForm.webhook_event_name,
        webhook_timeout_seconds: inferredForm.webhook_timeout_seconds || 600,
      }),
      ...(Object.keys(inferredForm.resp_data || {}).length > 0 && {
        resp_data: inferredForm.resp_data,
      }),
    };

    const hasPendingTemplate = Object.keys(inferredForm.resp_data || {}).some(
      (key) => inferredForm.resp_data[key]?.req_uid?.startsWith("temp_"),
    );

    const payload = {
      api_type: API_TYPE.REGULAR,
      scope: configScope,
      ...(configScope === SCOPE.APP ? { app_name: appName } : {}),
      api_config: apiConfigPayload,
    };
    if (force) payload.force = true;

    if (editingApiConfigId) {
      payload.api_config_id = editingApiConfigId;
    }

    if (hasPendingTemplate) {
      const finalResponseData = {};
      for (const [status, data] of Object.entries(
        inferredForm.resp_data || {},
      )) {
        if (data.req_uid?.startsWith("temp_")) {
          finalResponseData[status] = {
            status_code: status,
            kb_id: kbIdForTemplates,
            resp_template: inferredForm.response_template?.[status] || "",
            controls: {
              show_button: true,
              button_text: "Continue",
              theme: "dark",
              scroll_mode: "vertical",
              layout_direction: "vertical",
              data_type: "array",
              is_sample_array: true,
              array_layout_direction: "vertical",
            },
            transformation_steps: inferredForm.transformation_steps || [],
            actions: inferredForm.actions || [],
            text_answer_template: data.text_answer_template || "",
            is_active: true,
          };
        }
      }
      if (Object.keys(finalResponseData).length > 0) {
        payload.final_response_data = finalResponseData;
      }
    }

    return { payload, requiredStatusesForSave };
  };

  const performSave = async (force = false) => {
    const { payload, requiredStatusesForSave } = buildSavePayload(force);

    if (!validateRegularConfigAuth(inferredForm)) return;
    if (!validateAuthorizationHeader(inferredForm.headers)) return;

    const urlError = checkOutboundUrl(inferredForm.apiUrl);
    if (urlError) {
      openAlert({
        heading: "Invalid URL",
        paragraph: urlError,
        icon: <FaExclamationTriangle size={28} />,
        iconColor: "#d97706",
        iconBG: "#fef3c7",
      });
      return;
    }

    if (requiredStatusesForSave.length === 0) {
      openAlert({
        heading: "Missing status code",
        paragraph:
          "Please add at least one status code in Sample Data before saving.",
        icon: <FaExclamationTriangle size={28} />,
        iconColor: "#d97706",
        iconBG: "#fef3c7",
      });
      return;
    }

    const unconfigured = getUnconfiguredStatuses(
      inferredForm.resp_data,
      requiredStatusesForSave,
    );
    if (unconfigured.length > 0) {
      openAlert({
        heading: "Response template missing",
        paragraph:
          "Please create a Response Template for the following status code(s):\n\n" +
          `  • ${unconfigured.join("\n  • ")}\n\n` +
          "Click '🎨 Create for <status>' in the Inferred tab to open the Preview Tester and design the message users will see.",
        icon: <FaExclamationTriangle size={28} />,
        iconColor: "#d97706",
        iconBG: "#fef3c7",
      });
      return;
    }

    const missingTemplateString = requiredStatusesForSave.filter(
      (s) => !inferredForm.response_template?.[s],
    );
    if (missingTemplateString.length > 0) {
      openAlert({
        heading: "Template string missing",
        paragraph:
          "Some statuses are marked configured but their template string is missing:\n\n" +
          `  • ${missingTemplateString.join("\n  • ")}\n\n` +
          "Please reopen the Preview Tester and re-save the template.",
        icon: <FaExclamationTriangle size={28} />,
        iconColor: "#d97706",
        iconBG: "#fef3c7",
      });
      return;
    }

    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/api_config`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        },
      );

      if (resp.ok) {
        openAlert({
          heading: "Saved",
          paragraph: "API Configuration saved successfully!",
          icon: <FaCheckCircle size={28} />,
        });
        closeEditor();
        fetchApiConfigs();
        return;
      }

      let body = {};
      try {
        body = await resp.json();
      } catch (_) {
        body = {};
      }

      const refusal = classifyGuardRefusal(body);

      if (refusal.kind === "attached-in-use") {
        openGuardPopup({
          refusal,
          onForce: () => performSave(true),
        });
        return;
      }

      if (refusal.kind === "referenced") {
        openGuardPopup({ refusal });
        return;
      }

      if (refusal.kind === "attach-required") {
        openAlert({
          heading: "Attach required",
          paragraph: refusal.message,
          icon: <FaExclamationTriangle size={28} />,
          iconColor: "#d97706",
          iconBG: "#fef3c7",
        });
        return;
      }

      openAlert({
        heading: "Failed to save",
        paragraph:
          body?.error ||
          body?.detail ||
          `Save failed (HTTP ${resp.status})`,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    } catch (e) {
      openAlert({
        heading: "Error",
        paragraph:
          "Error saving configuration. Please check your inputs and try again.",
        icon: <FaExclamationTriangle size={28} />,
        iconColor: "#d97706",
        iconBG: "#fef3c7",
      });
    }
  };

  const handleSave = () => performSave(false);

  const performDelete = async (apiConfigId) => {
    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/api_config?api_config_id=${apiConfigId}`,
        {
          method: "DELETE",
          headers: { Authorization: token },
        },
      );
      if (resp.ok) {
        openAlert({
          heading: "Deleted",
          paragraph: "API Configuration deleted successfully.",
          icon: <RiDeleteBin6Line size={28} />,
          iconColor: "#dc2626",
          iconBG: "#fee2e2",
        });
        fetchApiConfigs();
        return;
      }

      let body = {};
      try {
        body = await resp.json();
      } catch (_) { }
      const refusal = classifyGuardRefusal(body);

      if (refusal.kind === "attached-in-use") {
        openGuardPopup({ refusal });
        return;
      }
      if (refusal.kind === "referenced") {
        openGuardPopup({ refusal });
        return;
      }

      openAlert({
        heading: "Failed to delete",
        paragraph:
          body?.error ||
          body?.detail ||
          `Delete failed (HTTP ${resp.status}).`,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    } catch (e) {
      openAlert({
        heading: "Error",
        paragraph: "Failed to delete configuration. Please try again",
        icon: <FaExclamationTriangle size={28} />,
        iconColor: "#d97706",
        iconBG: "#fef3c7",
      });
    }
  };

  const handleDelete = (apiConfigId) => {
    openConfirmPopup({
      heading: "Delete this API Configuration?",
      subtext: "This action cannot be undone.",
      paragraph:
        "This will permanently remove the configuration and may break any Skills or Forms that depend on it.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <RiDeleteBin6Line size={28} />,
      iconColor: "#dc2626",
      iconBG: "#ffc4c4",
      onConfirm: () => {
        closeConfirmPopup();
        performDelete(apiConfigId);
      },
    });
  };

  const handleWebhookToggle = (checked, editingId) => {
    if (checked) {
      const configId = editingId || `config_${Date.now()}`;
      setInferredForm((prev) => ({
        ...prev,
        webhook_event_name: `${configId}__event`,
        webhook_timeout_seconds: 600,
      }));
    } else {
      setInferredForm((prev) => ({
        ...prev,
        webhook_event_name: "",
        webhook_timeout_seconds: undefined,
      }));
    }
  };

  const requiredStatuses = Array.from(
    new Set([
      ...sampleResponses.map((r) => (r.status || "").trim()).filter(Boolean),
      ...Object.keys(inferredForm.response_template || {}),
    ]),
  );

  const unconfiguredStatuses = getUnconfiguredStatuses(
    inferredForm.resp_data,
    requiredStatuses,
  );

  const configuredStatuses = requiredStatuses.filter(
    (status) => !unconfiguredStatuses.includes(status),
  );

  const isTemplateValid =
    requiredStatuses.length > 0 && unconfiguredStatuses.length === 0;

  const getActiveStatus = () => {
    if (configActiveTab !== "inferred") return null;
    if (sampleResponses.length > 0) {
      return sampleResponses[0].status;
    }
    return null;
  };

  const activeStatusForConfig = getActiveStatus();
  const canSaveRegular = !!inferredForm.apiUrl && isTemplateValid;

  const kbIdForTemplates =
    appData?.tenant_id && appData?.app_name
      ? `${appData.tenant_id}__${appData.app_name}`
      : appData?.tenant_id
        ? `${appData.tenant_id}__global`
        : "";

  const activeList = activeTab === SCOPE.TENANT ? tenantConfigs : appConfigs;
  const tenantCount = tenantConfigs.length;
  const appCount = appConfigs.length;

  const overriddenByAppLevel = false;
  const attachCount = attachedIds.size;

  const canEditRow = (cfg) => cfg.scope !== SCOPE.TENANT || isGlobalContext;

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ fontSize: "20px", marginBottom: "10px" }}>⏳</div>
        <h3>Loading your API configurations...</h3>
        <p style={{ color: "#666" }}>
          Please wait while we fetch your settings.
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <div
        style={{
          color: "#c62828",
          padding: "20px",
          backgroundColor: "#ffebee",
          borderRadius: "8px",
        }}
      >
        <strong>❌ Error:</strong> {error}
        <p style={{ marginTop: "8px", fontSize: "14px" }}>
          Please refresh the page or contact support if the problem persists.
        </p>
      </div>
    );
  }

  const renderConfigCard = (cfg, { isTenantTab }) => {
    const attachBucket = attachmentsByConfig.get(cfg.api_config_id);
    const appNames = attachBucket?.app_names || [];
    const isAttachedByMe = attachedIds.has(cfg.api_config_id);
    const globalAdmin = isGlobalContext;

    let badgeKind;
    if (cfg.scope === SCOPE.APP) {
      badgeKind = "app";
    } else if (globalAdmin) {
      badgeKind =
        appNames.length > 0 ? "tenant-detached-blocked" : "tenant-unattached";
    } else {
      badgeKind = isAttachedByMe ? "tenant-attached" : "tenant-available";
    }

    const title = cfg.api_config?.url || cfg.api_config_id;

    return (
      <div key={cfg.api_config_id} className={styles.configCard}>
        <div className={styles.configCardHeader}>
          <div className={styles.configInfo}>
            <div className={styles.configId}>
              <ResourceScopeBadge
                kind={badgeKind}
                count={
                  globalAdmin && appNames.length > 0
                    ? appNames.length
                    : undefined
                }
                onClick={
                  globalAdmin && appNames.length > 0
                    ? () =>
                      openGuardPopup({
                        refusal: {
                          kind: "attached-in-use",
                          apps: appNames,
                          refs: null,
                          message: `This shared config is attached to ${appNames.length} app${appNames.length === 1 ? "" : "s"}: ${appNames.join(", ")}. Detach all apps before deleting.`,
                          canForce: false,
                          detail: "",
                        },
                      })
                    : undefined
                }
              />
              <span style={{ marginLeft: 8 }}>ID: {cfg.api_config_id}</span>
              {cfg.api_config?.webhook_event_name && (
                <span
                  style={{
                    marginLeft: "8px",
                    fontSize: "12px",
                    color: "#4CAF50",
                    backgroundColor: "#E8F5E9",
                    padding: "2px 8px",
                    borderRadius: "12px",
                  }}
                >
                  🔄 Async Callback: {cfg.api_config.webhook_event_name}
                </span>
              )}
            </div>
            <div className={styles.configEndpoint}>
              <span className={styles.configUrl}>{title}</span>
              <span className={styles.configMethod}>
                {" "}
                ({cfg.api_config?.method})
              </span>
            </div>
            <details className={styles.configDetails}>
              <summary className={styles.configSummary}>
                View Full Configuration
              </summary>
              <pre className={styles.configPre}>
                {JSON.stringify(cfg.api_config, null, 2)}
              </pre>
            </details>

            {isTenantTab && !globalAdmin && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {isAttachedByMe ? (
                  <button
                    type="button"
                    onClick={() => handleDetach(cfg)}
                    style={{
                      padding: "6px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#c05621",
                      backgroundColor: "#fffaf0",
                      border: "1px solid #fbd38d",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <FaUnlink size={12} /> Detach from this app
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleAttach(cfg)}
                    style={{
                      padding: "6px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#2b6cb0",
                      backgroundColor: "#ebf8ff",
                      border: "1px solid #90cdf4",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <FaLink size={12} /> Attach to this app
                  </button>
                )}
              </div>
            )}

            {(cfg.scope === SCOPE.APP || globalAdmin) && (
              <div style={{ marginTop: "20px" }} className={styles.actionsWrap}>
                <button
                  onClick={() => {
                    const statusCode = prompt(
                      "Enter the HTTP status code (e.g., 200, 201, 404) to create a template for:",
                    );
                    if (statusCode) {
                      const sampleResponse =
                        cfg.api_config?.sample_responses_by_status?.[
                        statusCode
                        ] || null;
                      handleCreateResponseTemplate(
                        cfg.api_config_id,
                        kbIdForTemplates,
                        statusCode,
                        sampleResponse,
                      );
                    }
                  }}
                  className={styles.createTemplateButton}
                  title="Create a response template for this API"
                >
                  🎨 Create Template
                </button>
                <button
                  onClick={() =>
                    handleViewAllTemplates(cfg.api_config_id, kbIdForTemplates)
                  }
                  className={styles.viewAllTemplatesButton}
                  title="View all response templates for this API"
                >
                  📋 View All Templates
                </button>
              </div>
            )}
          </div>

          <div className={styles.configActions}>
            {canEditRow(cfg) && (
              <button
                onClick={() => openEdit(cfg)}
                className={styles.editButton}
                title={
                  cfg.scope === SCOPE.TENANT && !globalAdmin
                    ? "You can attach/detach, but only the tenant admin can edit"
                    : "Modify this configuration"
                }
              >
                <BiSolidEdit size={22} />
              </button>
            )}
            {canEditRow(cfg) && (
              <button
                onClick={() => handleDelete(cfg.api_config_id)}
                className={styles.deleteButton}
                title="Remove this configuration"
              >
                <RiDeleteBin6Line size={22} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderActiveTabBody = () => {
    if (activeList.length === 0) {
      return (
        <div className={styles.emptyState}>
          {activeTab === SCOPE.TENANT ? (
            <>
              <p>
                {isGlobalContext
                  ? "No shared API configs yet. Create one to make it available to every app on this tenant."
                  : "Your tenant admin hasn't published any shared API configs yet."}
              </p>
              {isGlobalContext && (
                <p
                  style={{ fontSize: "13px", color: "#888", marginTop: "4px" }}
                >
                  💡 Shared configs are managed from the Global Level Settings
                  surface.
                </p>
              )}
            </>
          ) : (
            <>
              <p>No app-scoped API configurations yet. Create one above.</p>
              <p
                style={{ fontSize: "13px", color: "#888", marginTop: "4px" }}
              >
                💡 App-scoped configs are only visible to this app.
              </p>
            </>
          )}
        </div>
      );
    }

    const isTenantTab = activeTab === SCOPE.TENANT;
    return (
      <div className={styles.configsList}>
        {activeList.map((cfg) => renderConfigCard(cfg, { isTenantTab }))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <ScopeTabs
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          if (showCreateConfig) {
            closeEditor();
          }
        }}
        tenantCount={tenantCount}
        appCount={appCount}
        resourceNoun="config"
        overriddenByAppLevel={overriddenByAppLevel}
        attachCount={attachCount}
        disabled={loading}
        compact={false}
        hiddenTabs={isGlobalContext ? ["app"] : []}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <button
          onClick={fetchApiConfigs}
          disabled={loading}
          title="Reload configs"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 12,
            color: "#475569",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <FaSyncAlt size={11} /> Reload
        </button>
      </div>

      {activeTab === SCOPE.APP && !isGlobalContext && (
        <div className={styles.regularConfigsSection}>
          <div className={styles.configsHeader}>
            <div className={styles.tittleWrap}>
              <h3 className={styles.configsTitle}>App-level configs</h3>
              <span className={styles.infoIcon}>
                <FaInfoCircle size={16} />
                <span className={styles.tooltipText}>
                  These are the API endpoints this app calls. A config can be
                  used by multiple Skills or Forms on this app.
                </span>
              </span>
            </div>
            <button onClick={openCreate} className={styles.createConfigButton}>
              + Create New API
            </button>
          </div>

          {showCreateConfig && (
            <div ref={editorModalRef} style={{ scrollMarginTop: 80 }}>
              <div className={styles.configEditorModal}>
                <h4 className={styles.modalTitle}>
                  {editingApiConfigId
                    ? "Modify API Configuration"
                    : "New API Configuration"}
                </h4>

                <div className={styles.curlImportSection}>
                  <button
                    onClick={() => {
                      setCurlImportOpen(!curlImportOpen);
                      if (!curlImportOpen) {
                        setIsCurlImportCompleted(false);
                      }
                    }}
                    className={`${styles.curlToggleButton} ${curlImportOpen ? styles.active : ""}`}
                  >
                    {curlImportOpen ? "Hide cURL Importer" : "Paste cURL"}
                  </button>

                  <CurlImporterOverlay
                    isOpen={curlImportOpen}
                    onClose={() => {
                      setCurlImportOpen(false);
                      setIsCurlImportCompleted(false);
                    }}
                    onImport={handleCurlImport}
                    intentName="api_config_editor"
                    isConfigured={false}
                    title="Import API Configuration from cURL"
                  />
                </div>

                <CustomAPIConfig
                  ref={customConfigRef}
                  appData={appData}
                  apiConfigId={editingApiConfigId}
                  intentName="api_config_editor"
                  leadDest={{ type: "custom", config: inferredForm }}
                  selectedForm={null}
                  customFields={[]}
                  isConfigured={false}
                  sampleConfig={sampleForm}
                  onSampleChange={regularHandlers.onSampleChange}
                  onSampleHeaderChange={regularHandlers.onSampleHeaderChange}
                  onSampleAddHeader={regularHandlers.onSampleAddHeader}
                  onSampleRemoveHeader={regularHandlers.onSampleRemoveHeader}
                  onSampleQueryChange={regularHandlers.onSampleQueryChange}
                  onSampleAddQuery={regularHandlers.onSampleAddQuery}
                  onSampleRemoveQuery={regularHandlers.onSampleRemoveQuery}
                  sampleResponses={sampleResponses}
                  onSampleResponseChange={regularHandlers.onSampleResponseChange}
                  onAddSampleResponse={regularHandlers.onAddSampleResponse}
                  onRemoveSampleResponse={
                    regularHandlers.onRemoveSampleResponse
                  }
                  onSampleResponsesChange={setSampleResponses}
                  onSamplePayloadSync={handleSamplePayloadSyncFromChild}
                  onInfer={() => {
                    const changed = haveSampleResponsesChanged(
                      sampleResponses,
                      previousSampleResponses,
                    );
                    const preserve = editingApiConfigId && !changed;
                    inferVariables(
                      sampleForm,
                      sampleResponses,
                      setInferredForm,
                      setInferredReady,
                      setConfigActiveTab,
                      preserve,
                    );
                  }}
                  inferredConfig={inferredForm}
                  onInferredChange={regularHandlers.onInferredChange}
                  onInferredHeaderChange={regularHandlers.onInferredHeaderChange}
                  onInferredAddHeader={regularHandlers.onInferredAddHeader}
                  onInferredRemoveHeader={regularHandlers.onInferredRemoveHeader}
                  onInferredQueryChange={regularHandlers.onInferredQueryChange}
                  onInferredAddQuery={regularHandlers.onInferredAddQuery}
                  onInferredRemoveQuery={regularHandlers.onInferredRemoveQuery}
                  onInsertField={regularHandlers.onInsertField}
                  activeTab={configActiveTab}
                  onTabChange={setConfigActiveTab}
                  activeStatus={activeStatusForConfig}
                />

                {(isCurlImportCompleted || editingApiConfigId) && (
                  <>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        <strong>Wait for Async Callback (Webhook):</strong>
                        <span
                          style={{
                            marginLeft: "8px",
                            cursor: "help",
                            color: "#666",
                            display: "inline-block",
                          }}
                          title="Enable this if your API processes requests asynchronously. For example, a booking request that takes time to confirm. The system will wait for a callback from your API before continuing."
                        >
                          <FaInfoCircle size={14} />
                        </span>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginTop: "4px",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!inferredForm.webhook_event_name}
                            onChange={(e) =>
                              handleWebhookToggle(
                                e.target.checked,
                                editingApiConfigId,
                              )
                            }
                          />
                          Enable Async Callback
                        </label>
                      </div>
                      {inferredForm.webhook_event_name && (
                        <div style={{ marginTop: "10px" }}>
                          <label
                            style={{ display: "block", marginBottom: "4px" }}
                          >
                            Callback Event Name:
                          </label>
                          <input
                            type="text"
                            value={inferredForm.webhook_event_name}
                            disabled={true}
                            style={{
                              width: "100%",
                              padding: "6px",
                              fontSize: "14px",
                              backgroundColor: "#f5f5f5",
                              color: "#333",
                            }}
                          />
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              marginTop: "8px",
                            }}
                          >
                            Timeout (seconds):
                          </label>
                          <input
                            type="number"
                            value={inferredForm.webhook_timeout_seconds || 600}
                            onChange={(e) =>
                              setInferredForm((prev) => ({
                                ...prev,
                                webhook_timeout_seconds:
                                  parseInt(e.target.value) || 600,
                              }))
                            }
                            style={{
                              width: "100%",
                              padding: "6px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <UnconfiguredTemplatesBox
                        unconfiguredStatuses={unconfiguredStatuses}
                        configuredStatuses={configuredStatuses}
                        inferredForm={inferredForm}
                        sampleResponses={sampleResponses}
                        isTemplateValid={isTemplateValid}
                        requiredStatuses={requiredStatuses}
                        setInferredForm={setInferredForm}
                        apiConfigId={editingApiConfigId}
                        region={region}
                        token={token}
                        backend_api_base_url={backend_api_base_url}
                        onOpenScreenEditor={(status) => {
                          const sampleResponse =
                            inferredForm?.sample_responses_by_status?.[status] ||
                            sampleResponses.find((r) => r.status === status)?.body ||
                            null;
                          handleCreateResponseTemplate(
                            editingApiConfigId || null,
                            kbIdForTemplates,
                            status,
                            sampleResponse,
                          );
                        }}
                      />
                    </div>
                  </>
                )}

                <div className={styles.modalActions}>
                  <button
                    onClick={handleSave}
                    disabled={!canSaveRegular}
                    className={styles.saveButton}
                    style={{
                      opacity: canSaveRegular ? 1 : 0.5,
                      cursor: canSaveRegular ? "pointer" : "not-allowed",
                    }}
                    title={
                      !inferredForm.apiUrl
                        ? "Please provide the API URL first"
                        : unconfiguredStatuses.length > 0
                          ? `Template not yet created for status: ${unconfiguredStatuses.join(", ")}`
                          : ""
                    }
                  >
                    Save Configuration
                  </button>
                  <button onClick={closeEditor} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {renderActiveTabBody()}
        </div>
      )}

      {activeTab === SCOPE.TENANT && (
        <div className={styles.regularConfigsSection}>
          <div className={styles.configsHeader}>
            <div className={styles.tittleWrap}>
              <h3 className={styles.configsTitle}>
                {isGlobalContext
                  ? "Tenant-level configs"
                  : "Shared configs from your tenant"}
              </h3>
              <span className={styles.infoIcon}>
                <FaInfoCircle size={16} />
                <span className={styles.tooltipText}>
                  {isGlobalContext
                    ? "These configs are shared by every app on the tenant. Apps must explicitly attach them before they can be used."
                    : "Attach a shared config to make it selectable from this app's Skills and Forms. The tenant admin can detach it later, which will break anything on this app that uses it."}
                </span>
              </span>
            </div>

            {!isGlobalContext && (
              <button
                type="button"
                className={styles.createConfigButton}
                disabled
                title="Tenant-level configs are created from the Global Level Settings surface, not from an app's API Config tab."
                style={{
                  opacity: 0.5,
                  cursor: "not-allowed",
                }}
              >
                + Create New API
              </button>
            )}

            {isGlobalContext && (
              <button
                type="button"
                className={styles.createConfigButton}
                onClick={openCreate}
              >
                + Create New API
              </button>
            )}
          </div>

          {showCreateConfig && isGlobalContext && (
            <div ref={editorModalRef} style={{ scrollMarginTop: 80 }}>
              <div className={styles.configEditorModal}>
                <h4 className={styles.modalTitle}>
                  {editingApiConfigId
                    ? "Modify API Configuration"
                    : "New API Configuration"}
                </h4>

                <div className={styles.curlImportSection}>
                  <button
                    onClick={() => {
                      setCurlImportOpen(!curlImportOpen);
                      if (!curlImportOpen) {
                        setIsCurlImportCompleted(false);
                      }
                    }}
                    className={`${styles.curlToggleButton} ${curlImportOpen ? styles.active : ""}`}
                  >
                    {curlImportOpen ? "Hide cURL Importer" : "Paste cURL"}
                  </button>

                  <CurlImporterOverlay
                    isOpen={curlImportOpen}
                    onClose={() => {
                      setCurlImportOpen(false);
                      setIsCurlImportCompleted(false);
                    }}
                    onImport={handleCurlImport}
                    intentName="api_config_editor"
                    isConfigured={false}
                    title="Import API Configuration from cURL"
                  />
                </div>

                <CustomAPIConfig
                  ref={customConfigRef}
                  appData={appData}
                  apiConfigId={editingApiConfigId}
                  intentName="api_config_editor"
                  leadDest={{ type: "custom", config: inferredForm }}
                  selectedForm={null}
                  customFields={[]}
                  isConfigured={false}
                  sampleConfig={sampleForm}
                  onSampleChange={regularHandlers.onSampleChange}
                  onSampleHeaderChange={regularHandlers.onSampleHeaderChange}
                  onSampleAddHeader={regularHandlers.onSampleAddHeader}
                  onSampleRemoveHeader={regularHandlers.onSampleRemoveHeader}
                  onSampleQueryChange={regularHandlers.onSampleQueryChange}
                  onSampleAddQuery={regularHandlers.onSampleAddQuery}
                  onSampleRemoveQuery={regularHandlers.onSampleRemoveQuery}
                  sampleResponses={sampleResponses}
                  onSampleResponseChange={regularHandlers.onSampleResponseChange}
                  onAddSampleResponse={regularHandlers.onAddSampleResponse}
                  onRemoveSampleResponse={
                    regularHandlers.onRemoveSampleResponse
                  }
                  onSampleResponsesChange={setSampleResponses}
                  onSamplePayloadSync={handleSamplePayloadSyncFromChild}
                  onInfer={() => {
                    const changed = haveSampleResponsesChanged(
                      sampleResponses,
                      previousSampleResponses,
                    );
                    const preserve = editingApiConfigId && !changed;
                    inferVariables(
                      sampleForm,
                      sampleResponses,
                      setInferredForm,
                      setInferredReady,
                      setConfigActiveTab,
                      preserve,
                    );
                  }}
                  inferredConfig={inferredForm}
                  onInferredChange={regularHandlers.onInferredChange}
                  onInferredHeaderChange={regularHandlers.onInferredHeaderChange}
                  onInferredAddHeader={regularHandlers.onInferredAddHeader}
                  onInferredRemoveHeader={regularHandlers.onInferredRemoveHeader}
                  onInferredQueryChange={regularHandlers.onInferredQueryChange}
                  onInferredAddQuery={regularHandlers.onInferredAddQuery}
                  onInferredRemoveQuery={regularHandlers.onInferredRemoveQuery}
                  onInsertField={regularHandlers.onInsertField}
                  activeTab={configActiveTab}
                  onTabChange={setConfigActiveTab}
                  activeStatus={activeStatusForConfig}
                />

                {(isCurlImportCompleted || editingApiConfigId) && (
                  <>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        <strong>Wait for Async Callback (Webhook):</strong>
                        <span
                          style={{
                            marginLeft: "8px",
                            cursor: "help",
                            color: "#666",
                            display: "inline-block",
                          }}
                          title="Enable this if your API processes requests asynchronously. For example, a booking request that takes time to confirm. The system will wait for a callback from your API before continuing."
                        >
                          <FaInfoCircle size={14} />
                        </span>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginTop: "4px",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!inferredForm.webhook_event_name}
                            onChange={(e) =>
                              handleWebhookToggle(
                                e.target.checked,
                                editingApiConfigId,
                              )
                            }
                          />
                          Enable Async Callback
                        </label>
                      </div>
                      {inferredForm.webhook_event_name && (
                        <div style={{ marginTop: "10px" }}>
                          <label
                            style={{ display: "block", marginBottom: "4px" }}
                          >
                            Callback Event Name:
                          </label>
                          <input
                            type="text"
                            value={inferredForm.webhook_event_name}
                            disabled={true}
                            style={{
                              width: "100%",
                              padding: "6px",
                              fontSize: "14px",
                              backgroundColor: "#f5f5f5",
                              color: "#333",
                            }}
                          />
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              marginTop: "8px",
                            }}
                          >
                            Timeout (seconds):
                          </label>
                          <input
                            type="number"
                            value={inferredForm.webhook_timeout_seconds || 600}
                            onChange={(e) =>
                              setInferredForm((prev) => ({
                                ...prev,
                                webhook_timeout_seconds:
                                  parseInt(e.target.value) || 600,
                              }))
                            }
                            style={{
                              width: "100%",
                              padding: "6px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <UnconfiguredTemplatesBox
                        unconfiguredStatuses={unconfiguredStatuses}
                        configuredStatuses={configuredStatuses}
                        inferredForm={inferredForm}
                        sampleResponses={sampleResponses}
                        isTemplateValid={isTemplateValid}
                        requiredStatuses={requiredStatuses}
                        setInferredForm={setInferredForm}
                        apiConfigId={editingApiConfigId}
                        region={region}
                        token={token}
                        backend_api_base_url={backend_api_base_url}
                        onOpenScreenEditor={(status) => {
                          const sampleResponse =
                            inferredForm?.sample_responses_by_status?.[status] ||
                            sampleResponses.find((r) => r.status === status)?.body ||
                            null;
                          handleCreateResponseTemplate(
                            editingApiConfigId || null,
                            kbIdForTemplates,
                            status,
                            sampleResponse,
                          );
                        }}
                      />
                    </div>
                  </>
                )}

                <div className={styles.modalActions}>
                  <button
                    onClick={handleSave}
                    disabled={!canSaveRegular}
                    className={styles.saveButton}
                    style={{
                      opacity: canSaveRegular ? 1 : 0.5,
                      cursor: canSaveRegular ? "pointer" : "not-allowed",
                    }}
                    title={
                      !inferredForm.apiUrl
                        ? "Please provide the API URL first"
                        : unconfiguredStatuses.length > 0
                          ? `Template not yet created for status: ${unconfiguredStatuses.join(", ")}`
                          : ""
                    }
                  >
                    Save Configuration
                  </button>
                  <button onClick={closeEditor} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {renderActiveTabBody()}
        </div>
      )}

      <ReusablePopup
        isOpen={confirmPopup.isOpen}
        onClose={closeConfirmPopup}
        onConfirm={confirmPopup.onConfirm}
        icon={confirmPopup.icon}
        iconColor={confirmPopup.iconColor}
        iconBG={confirmPopup.iconBG}
        heading={confirmPopup.heading}
        subtext={confirmPopup.subtext}
        paragraph={confirmPopup.paragraph}
        cancelText={confirmPopup.cancelText}
        confirmText={confirmPopup.confirmText}
        showCancel={!!confirmPopup.cancelText}
        showConfirm={true}
      />
    </div>
  );
}